import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { connect } from 'react-redux'
import {
  ChevronDown,
  ChevronUp
} from 'lucide-react'
import styles from './Faq.module.scss'
import BotoneraModern from '../../components/Botonera/BotoneraModern.tsx'
import useNavVisibility from '../../hooks/useNavVisibility.ts'
import { openTutorialModal } from '../../store/actions'

type DispatchProps = {
  openTutorialModal: () => void
}

type AnswerBlock =
  | { type: 'paragraph'; content: string }
  | { type: 'list'; items: string[] }

type FaqItem = {
  id: string
  question: string
  answer: AnswerBlock[]
}

const Container = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding-right: 2%;
  padding-left: 2%;
  max-width: 150rem;
  margin: auto;
  @media (max-width: 1200px) {
    padding-right: 2%;
    padding-left: 2%;
  }
  @media (max-width: 900px) {
    padding-right: 0;
    padding-left: 0;
  }

  @media (max-width: 600px) {
    padding-right: 0;
    padding-left: 0;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  width: 100%;
  margin: 0 auto;
  padding: 2rem 0;


  @media (max-width: 900px) {
  padding-top: 8rem;
  padding-left: 2rem;
  padding-right: 2rem;
  }

  @media (max-width: 600px) {
    padding-top: 8rem;
    padding-left: 2rem;
    padding-right: 2rem;
  }

`

const TutorialNotice = styled.div`
  padding: 1.2rem 1.6rem;
  border-radius: 1.2rem;
  background: rgba(35, 75, 162, 0.08);
  color: #0f172a;
  font-size: 1.3rem;
  text-align: left;
`

const Faq: React.FC<DispatchProps> = ({ openTutorialModal }) => {
  const { ocultarEnMobile, mobileStylesFromParent } = useNavVisibility()
  const navigate = useNavigate()

  const faqItems = useMemo<FaqItem[]>(
    () => [
      {
        id: 'inicio',
        question: '¿Cómo empiezo a usar Edify?',
        answer: [
          {
            type: 'paragraph',
            content:
              'En Inicio tenés al agente de IA de Edify: es tu forma principal de interactuar con la plataforma. Escribile qué querés hacer (por ejemplo “necesito presupuestos para mi obra”, “quiero pedir una cotización de materiales”, “busco profesionales” o “quiero explorar servicios”) y el agente te guía o te lleva a la sección correcta.'
          },
          {
            type: 'list',
            items: [
              'Completá tu perfil y elegí los rubros que te interesan para recibir recomendaciones acertadas.',
              'Actualizá tus datos de contacto para que otros usuarios puedan encontrarte.',
              'Si preferís navegar por tu cuenta, usá Publicar, Explorar, Mis contenidos y Herramientas desde Inicio.'
            ]
          }
        ]
      },
      {
        id: 'obras',
        question: '¿Cómo publico una obra privada y recibo presupuestos?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Decile al agente de Edify que querés publicar una obra y recibir presupuestos; te va a llevar al formulario y puede ayudarte a completarlo. También podés ir por Publicar → Presupuestos para mi obra. Cargá el proyecto con su documentación y definí los rubros que necesitás; cada rubro puede tener un plazo y condiciones propias.'
          },
          {
            type: 'list',
            items: [
              'Compartí planos, memorias descriptivas o planillas para mejorar la calidad de las cotizaciones.',
              'Elegí quiénes pueden acceder a la documentación y seguí las propuestas desde Notificaciones.',
              'Confirmá qué proveedor contratás y cerrá la gestión directamente con él.'
            ]
          }
        ]
      },
      {
        id: 'cotizaciones-materiales',
        question: '¿Cómo solicito o respondo cotizaciones de materiales?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Pedile al agente que te ayude a pedir una cotización de materiales o a explorar cotizaciones abiertas para enviar tu propuesta; te guía en el proceso. Todo el intercambio se gestiona dentro de la plataforma.'
          },
          {
            type: 'list',
            items: [
              'Para hacerlo por tu cuenta: Publicar → Pedir una cotización para crear una solicitud; Explorar → Explorar cotizaciones para responder a pedidos.',
              'Publicá el detalle de materiales, plazos y adjuntá tu plantilla de costos.',
              'Recibí presupuestos en Notificaciones y administralos por proyecto.',
              'Si sos proveedor, descargá la plantilla, completá tu oferta y subila nuevamente.'
            ]
          }
        ]
      },
      {
        id: 'talento',
        question: '¿Cómo encuentro profesionales o publico oportunidades laborales?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Decile al agente qué necesitás: por ejemplo “busco profesionales de instalaciones sanitarias”, “quiero publicar una vacante” o “quiero ver mis postulaciones”. Te lleva a Explorar perfiles, a publicar una vacante o a Mis postulaciones según lo que pediste.'
          },
          {
            type: 'list',
            items: [
              'Edify conecta profesionales con empresas. Utilizá los filtros por rubro y ubicación para afinar la búsqueda.',
              'Si preferís navegar: Explorar → Explorar perfiles o Postularme a ofertas de trabajo; Publicar → Publicar vacante; Mis contenidos → Mis postulaciones.',
              'Actualizá tu perfil con referencias para mejorar tus postulaciones.'
            ]
          }
        ]
      },
      {
        id: 'marketplace',
        question: '¿Qué puedo hacer en el Marketplace?',
        answer: [
          {
            type: 'paragraph',
            content:
              'Pedile al agente que te muestre servicios o productos de la comunidad, o que te ayude a ofrecer los tuyos. El Marketplace es un canal para generar contactos y nuevas oportunidades comerciales.'
          },
          {
            type: 'list',
            items: [
              'Cargá fichas con fotos, descripciones y precios orientativos. Para gestionar tus publicaciones o explorar la oferta: decile al agente o usá Mis contenidos → Mis productos/servicios y Explorar → Explorar posteos, servicios y productos.'
            ]
          }
        ]
      },
      {
        id: 'documentacion',
        question: '¿Cómo gestiono o comercializo documentación de obra?',
        answer: [
          {
            type: 'paragraph',
            content:
              'La sección de documentación de obra está en desarrollo; cuando esté lista, el agente te va a poder guiar para subir y comercializar documentación de tus proyectos. Por ahora podés pedirle al agente que te lleve a Presupuestos para mi obra y compartir documentación con quienes cotizan en tu proyecto.'
          }
        ]
      },
      {
        id: 'tutorial',
        question: '¿Dónde accedo al tutorial interactivo?',
        answer: [
          {
            type: 'paragraph',
            content:
              'El tutorial te muestra cómo usar el agente de IA de Edify y las secciones esenciales de la plataforma. Podés iniciarlo en cualquier momento desde esta página con el botón “Ver tutorial interactivo”; te lleva a Inicio y allí se abre el tutorial.'
          }
        ]
      }
    ],
    []
  )

  const [openItem, setOpenItem] = useState<string | null>(faqItems[0]?.id ?? null)
  const handleOpenTutorial = () => {
    navigate('/dashboard')
    window.setTimeout(() => {
      openTutorialModal()
    }, 250)
  }

  return (
    <Container>
      {ocultarEnMobile && <BotoneraModern mobileStylesFromParent={mobileStylesFromParent} />}
      <Wrapper>
        <section className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>Preguntas frecuentes</h1>
            <p className={styles.subtitle}>
              Reunimos los pasos principales para que uses el agente de IA de Edify y gestiones obras, materiales, profesionales y marketplace.
            </p>
          </div>
          {/* Tutorial no disponible por ahora
          {!ocultarEnMobile ? (
            <button type="button" className={styles.tutorialButton} onClick={handleOpenTutorial}>
              Ver tutorial interactivo
            </button>
          ) : (
            <TutorialNotice>
              El tutorial interactivo está disponible para pantallas grandes. Si necesitás recorrerlo, ingresá desde una
              computadora o tablet en modo horizontal.
            </TutorialNotice>
          )}
          */}
        </section>

        <section className={styles.accordion}>
          {faqItems.map((item) => {
            const isOpen = openItem === item.id
            return (
              <div key={item.id} className={styles.item}>
                <button
                  type="button"
                  className={styles.question}
                  aria-expanded={isOpen}
                  onClick={() => setOpenItem(isOpen ? null : item.id)}
                >
                  <span>{item.question}</span>
                  {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                <div className={`${styles.answer} ${isOpen ? styles.open : ''}`} aria-hidden={!isOpen}>
                  {item.answer.map((block, index) => {
                    if (block.type === 'paragraph') {
                      return (
                        <p key={`${item.id}-p-${index}`} className={styles.answerParagraph}>
                          {block.content}
                        </p>
                      )
                    }
                    return (
                      <ul key={`${item.id}-l-${index}`} className={styles.answerList}>
                        {block.items.map((text, idx) => (
                          <li key={`${item.id}-li-${idx}`}>{text}</li>
                        ))}
                      </ul>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </section>
      </Wrapper>
    </Container>
  )
}

const mapStateToProps = () => ({})

export default connect(mapStateToProps, {
  openTutorialModal
})(Faq)

